import { useEffect, useState } from 'react'
import { DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { EligibilityResponse, ProgramResponse, RuleResponse } from '@/shared/api/types'
import { composeDefinition, getEligibility, listRules, publishRule, recomputeEligibility } from '../api'
import { RuleBuilder, hasEmptyGroup, type RuleNode } from '../RuleBuilder'

/**
 * Targeting: the rule versions, the builder, and the eligible pool they define.
 *
 * ## Rule sets are versioned and a correction is a NEW version
 *
 * The table is append-only. Somebody widening the age band in March publishes
 * v3; February's enrolments still record that they matched v2, and February's
 * eligibility snapshot still names v2's hash. Without that, every March
 * enrolment retroactively looks as though it matched the new rules and "why was
 * this person enrolled" has no answer.
 *
 * ## The eligible pool is a real number, and it is what coverage divides by
 *
 * PollSphere's funnel opened with `ELIGIBLE = every already-enrolled row`, so
 * its enrolment rate was ~100% by construction and coverage — the single most
 * important programme metric — did not exist.
 */
export function TargetingPanel({ program, onChanged }: {
  program: ProgramResponse; onChanged?: () => void
}) {
  const canUpdate = useCan('program:update')
  const [rules, setRules] = useState<RuleResponse[] | null>(null)
  const [eligibility, setEligibility] = useState<EligibilityResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [definition, setDefinition] = useState<RuleNode | null>(null)
  const [groupKeys, setGroupKeys] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => {
    listRules(program.id).then(setRules).catch((e) => setError(explain(e)))
    getEligibility(program.id).then(setEligibility).catch(() => setEligibility(null))
  }

  useEffect(() => {
    let alive = true
    listRules(program.id)
      .then((r) => { if (alive) { setRules(r); const live = r.length
          ? r.reduce((best, x) => (x.ruleVersion > best.ruleVersion ? x : best))
          : undefined
        if (live) { setDefinition(live.definition as unknown as RuleNode); setGroupKeys(live.targetGroupKeys ?? []) } } })
      .catch((e) => { if (alive) setError(explain(e)) })
    getEligibility(program.id)
      .then((e) => { if (alive) setEligibility(e) })
      .catch(() => { if (alive) setEligibility(null) })
    return () => { alive = false }
  }, [program.id])

  /**
   * Compose FIRST, then publish.
   *
   * `POST /{id}/rules` refuses an empty `definition` — `targetGroupKeys` on that
   * request is PROVENANCE, not the rule. So publishing a preset-only selection
   * used to send `{"definition":{},"targetGroupKeys":["MAMA_MBOGA",…]}` and take
   * a 400 every single time, while the button sat there enabled: the commonest
   * way to build a rule was the one that could never succeed.
   *
   * `/targeting/compose` is the server's own combiner — the same one the preview
   * uses. Composing the OR-of-presets here instead would be a second copy of the
   * grammar, and the two would drift.
   */
  const publish = async () => {
    setBusy(true); setError(null)
    try {
      const composed = groupKeys.length > 0
        ? await composeDefinition(groupKeys, definition ?? undefined)
        : (definition as unknown as Record<string, unknown>)
      await publishRule(program.id, composed, groupKeys, note || undefined)
      setEditing(false); setNote(''); load(); onChanged?.()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  const recompute = async () => {
    setBusy(true)
    try { setEligibility(await recomputeEligibility(program.id)) }
    catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  if (!rules) return <LoadingBlock label="Loading targeting…" />

  // The newest rule by VERSION. Nothing in `RuleResponse` or the controller
  // promises an ordering, and `rules[0]` quietly becomes "whatever the database
  // returned first" the day somebody adds an index.
  const live = rules.length
    ? rules.reduce((best, r) => (r.ruleVersion > best.ruleVersion ? r : best))
    : undefined
  const staleAgainstRule = eligibility && live && eligibility.ruleHash !== live.ruleHash

  return (
    <div className="space-y-4">
      <ErrorStrip message={error} />

      <DrawerSection
        title="Eligible pool"
        right={canUpdate && eligibility ? (
          <button type="button" className="erp-btn h-7 text-erp-xs" onClick={recompute} disabled={busy}>
            Recompute
          </button>
        ) : undefined}
      >
        <div className="px-4 py-3 space-y-2">
          {!eligibility ? (
            <p className="text-erp-sm text-muted-foreground">
              No eligibility snapshot yet. Publish a targeting rule version to compute one —
              until then this programme has no coverage denominator.
            </p>
          ) : (
            <>
              <p className="text-erp-2xl font-semibold tabular-nums">
                {fmtNumber(eligibility.eligibleCount)}
              </p>
              <p className="text-erp-xs text-muted-foreground">
                people match rule v{eligibility.ruleVersion}, computed{' '}
                {fmtDateTime(eligibility.computedAt)}.
              </p>
              {/* A pool above the shortlist cap has a REAL count and NO member
                  rows. Shortlisting from it would return 200 and create nobody,
                  so the screen says which of the two states this is. */}
              {!eligibility.materialised && (
                <Note tone="info">
                  The count is real, but individual rows were not materialised — this pool is
                  above the shortlist cap. Bulk shortlisting is unavailable until the rule is
                  narrowed or the cap is raised.
                </Note>
              )}
              {eligibility.stale && (
                <Note tone="bad">
                  This snapshot is stale
                  {eligibility.staleReasonCode === 'AGE' ? ' (too old)' : ''}. Coverage figures
                  computed from it are comparing today's numerator against an older denominator.
                </Note>
              )}
              {staleAgainstRule && (
                <Note tone="bad">
                  The targeting rule has changed since this snapshot was taken. Recompute before
                  reading coverage.
                </Note>
              )}
            </>
          )}
        </div>
      </DrawerSection>

      <DrawerSection
        title="Rule versions"
        right={canUpdate && !editing ? (
          <button type="button" className="erp-btn h-7 text-erp-xs" onClick={() => setEditing(true)}>
            Publish a new version
          </button>
        ) : undefined}
      >
        {rules.length === 0 ? (
          <p className="px-4 py-3 text-erp-sm text-muted-foreground">
            No targeting published. This programme cannot be activated yet.
          </p>
        ) : (
          <ul className="divide-y divide-border/60">
            {rules.map((r) => (
              <li key={r.id} className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-erp-sm font-medium">v{r.ruleVersion}</span>
                  {/* The ACTIVE one, not the first one. Labelling by array
                      position while the rest of the panel derives `live` by
                      version let the two disagree on the same screen. */}
                  {r.id === live?.id && (
                    <span className="text-erp-xs text-chocolate">active</span>
                  )}
                  <span className="ml-auto text-erp-xs text-muted-foreground">
                    {fmtDateTime(r.publishedAt)}
                  </span>
                </div>
                {r.note && <p className="text-erp-xs text-muted-foreground">{r.note}</p>}
                <p className="text-erp-xs text-muted-foreground">
                  {(r.targetGroupKeys ?? []).length
                    ? `Groups: ${r.targetGroupKeys.join(', ')}`
                    : 'No preset groups'}
                  {r.matchedEstimate !== null && r.matchedEstimate !== undefined
                    ? ` · matched ${fmtNumber(r.matchedEstimate)} at publish`
                    : ' · no estimate recorded'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </DrawerSection>

      {editing && (
        <DrawerSection title="New rule version">
          <div className="px-4 py-3 space-y-3">
            <Note tone="info">
              Publishing creates <strong>v{(live?.ruleVersion ?? 0) + 1}</strong>. Earlier
              versions are never edited — enrolments that matched them keep saying so.
            </Note>
            <RuleBuilder
              value={definition}
              onChange={setDefinition}
              groupKeys={groupKeys}
              onGroupKeysChange={setGroupKeys}
              scopeUnitId={program.scopeUnitId}
            />
            <label className="block space-y-1">
              <span className="text-erp-xs font-medium text-muted-foreground">
                Why this version? (recorded on the rule, permanently)
              </span>
              <input className="erp-input" value={note} maxLength={512}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Widened the age band after the ward review" />
            </label>
            <div className="flex gap-2">
              <button type="button" className="erp-btn" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button
                type="button" className="erp-btn-primary"
                disabled={busy || hasEmptyGroup(definition)
                  || (!definition && groupKeys.length === 0)}
                title={hasEmptyGroup(definition)
                  ? 'An empty group matches everybody in scope.' : undefined}
                onClick={publish}
              >
                {busy ? 'Publishing…' : 'Publish version'}
              </button>
            </div>
          </div>
        </DrawerSection>
      )}
    </div>
  )
}
